// src/services/vitalSignsService.js
import apiClient from "./apiClient";

const API_URL = `/vitalsigns`;

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && typeof data === "object") return [data];
  return [];
};

const sortByNewest = (list) =>
  [...list].sort((a, b) => {
    const aTime = new Date(a.recordedAt || a.createdAt || a.updatedAt || 0).getTime();
    const bTime = new Date(b.recordedAt || b.createdAt || b.updatedAt || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return Number(b.id || b.vitalSignId || 0) - Number(a.id || a.vitalSignId || 0);
  });

export const normalizeVitalSignsRecord = (vital) => {
  if (!vital || typeof vital !== "object") return null;
  return {
    id: vital.id ?? vital.vitalSignId ?? null,
    patientId: vital.patientId ?? vital.patient_id ?? null,
    appointmentId: vital.appointmentId ?? vital.appointment_id ?? null,
    prescriptionId: vital.prescriptionId ?? vital.prescription_id ?? null,
    bpReading: vital.bpReading ?? vital.bp_reading ?? "",
    weightReading: vital.weightReading ?? vital.weight_reading ?? "",
    heightReading: vital.heightReading ?? vital.height_reading ?? "",
    oxygenSaturation: vital.oxygenSaturation ?? vital.oxygen_saturation ?? "",
    pulseRate: vital.pulseRate ?? vital.pulse_rate ?? "",
    respirationRate: vital.respirationRate ?? vital.respiration_rate ?? "",
    recordedAt: vital.recordedAt ?? vital.recorded_at ?? vital.createdAt ?? null,
    source: vital.source ?? vital.recordedSource ?? null,
  };
};

/**
 * True when vital is linked to a prescription (doctor-submitted / post-prescription).
 * Front-desk entries typically have null / 0 / missing prescriptionId.
 */
export const hasVitalSignsPrescriptionId = (vital) => {
  if (!vital || typeof vital !== "object") return false;
  const raw =
    vital.prescriptionId ??
    vital.prescription_id ??
    null;
  if (raw === null || raw === undefined || raw === "") return false;
  const n = Number(raw);
  if (Number.isNaN(n)) return String(raw).trim().length > 0;
  return n > 0;
};

/** Display/report filter: hide front-desk (no prescription) rows to avoid duplicate OPD entries. */
export const filterVitalSignsWithPrescriptionId = (list) =>
  (Array.isArray(list) ? list : []).filter(hasVitalSignsPrescriptionId);

const toVitalDateKey = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw) && raw.length >= 3) {
    const [year, month, day] = raw;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Timeline display: keep front-desk vitals until a prescription-linked vital exists
 * for the same calendar day (or same appointment). Then show only prescription vitals
 * for that day/appointment to avoid duplicates.
 */
export const filterVitalSignsPreferPrescription = (list) => {
  const vitals = (Array.isArray(list) ? list : []).filter(Boolean);
  if (vitals.length === 0) return [];

  const withRx = vitals.filter(hasVitalSignsPrescriptionId);
  const withoutRx = vitals.filter((v) => !hasVitalSignsPrescriptionId(v));

  if (withRx.length === 0) return withoutRx;
  if (withoutRx.length === 0) return withRx;

  const rxDateKeys = new Set(
    withRx
      .map((v) => toVitalDateKey(v.recordedAt || v.createdAt || v.created_at))
      .filter(Boolean)
  );
  const rxAppointmentIds = new Set(
    withRx
      .map((v) => v.appointmentId ?? v.appointment_id)
      .filter((id) => id != null && id !== "")
      .map(String)
  );

  const frontDeskKept = withoutRx.filter((v) => {
    const appointmentId = v.appointmentId ?? v.appointment_id;
    if (
      appointmentId != null &&
      appointmentId !== "" &&
      rxAppointmentIds.has(String(appointmentId))
    ) {
      return false;
    }
    const dateKey = toVitalDateKey(v.recordedAt || v.createdAt || v.created_at);
    if (dateKey && rxDateKeys.has(dateKey)) {
      return false;
    }
    return true;
  });

  return [...withRx, ...frontDeskKept];
};

const storageKeyForAppointment = (appointmentId) =>
  `hms_frontdesk_vitals_${appointmentId}`;

/** Local same-browser fallback when backend endpoint is not yet available. */
export const cacheFrontDeskVitalSigns = (appointmentId, vitalSigns) => {
  if (!appointmentId || !vitalSigns) return;
  try {
    sessionStorage.setItem(
      storageKeyForAppointment(appointmentId),
      JSON.stringify({
        ...vitalSigns,
        appointmentId,
        cachedAt: new Date().toISOString(),
      })
    );
  } catch {
    // ignore storage errors
  }
};

export const readCachedFrontDeskVitalSigns = (appointmentId) => {
  if (!appointmentId) return null;
  try {
    const raw = sessionStorage.getItem(storageKeyForAppointment(appointmentId));
    if (!raw) return null;
    return normalizeVitalSignsRecord(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const getVitalSignsByPatientId = async (patientId) => {
  const response = await apiClient.get(`${API_URL}/patient/${patientId}`);
  return asArray(response.data).map(normalizeVitalSignsRecord).filter(Boolean);
};

export const getVitalSignsByAppointmentId = async (appointmentId) => {
  if (!appointmentId) return [];
  try {
    const response = await apiClient.get(`${API_URL}/appointment/${appointmentId}`);
    return asArray(response.data).map(normalizeVitalSignsRecord).filter(Boolean);
  } catch (error) {
    if (error?.response?.status === 404) return [];
    throw error;
  }
};

/**
 * Create OPD / front-desk vital signs (no prescription required).
 * Backend contract (org-scoped via apiClient organizationId query):
 * POST /vitalsigns
 * {
 *   patientId, appointmentId?, prescriptionId?: null,
 *   bpReading, weightReading, heightReading, pulseRate,
 *   respirationRate, oxygenSaturation, recordedAt,
 *   source: "FRONT_DESK", recordedByUserId?
 * }
 */
export const createVitalSigns = async (vitalData) => {
  const payload = {
    patientId: vitalData.patientId != null ? Number(vitalData.patientId) : null,
    appointmentId:
      vitalData.appointmentId != null ? Number(vitalData.appointmentId) : null,
    prescriptionId: vitalData.prescriptionId ?? null,
    bpReading: vitalData.bpReading ?? "",
    weightReading: vitalData.weightReading ?? "",
    heightReading: vitalData.heightReading ?? "",
    oxygenSaturation: vitalData.oxygenSaturation ?? "",
    pulseRate: vitalData.pulseRate ?? "",
    respirationRate: vitalData.respirationRate ?? "",
    recordedAt: vitalData.recordedAt || new Date().toISOString(),
    source: vitalData.source || "FRONT_DESK",
    ...(vitalData.recordedByUserId != null
      ? { recordedByUserId: Number(vitalData.recordedByUserId) }
      : {}),
  };

  const endpoints = [
    API_URL,
    `${API_URL}/create`,
    `/vital-signs`,
    `/vital-signs/create`,
  ];

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      const response = await apiClient.post(endpoint, payload);
      const saved = normalizeVitalSignsRecord(response.data) || payload;
      if (payload.appointmentId) {
        cacheFrontDeskVitalSigns(payload.appointmentId, saved);
      }
      return saved;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status && status !== 404 && status !== 405) {
        break;
      }
    }
  }

  // Soft fallback so frontdesk flow still works before backend ships create endpoint
  if (payload.appointmentId) {
    cacheFrontDeskVitalSigns(payload.appointmentId, payload);
    return { ...payload, cachedOnly: true };
  }

  throw lastError || new Error("Failed to save vital signs");
};

/**
 * Prefer appointment-linked vitals, then latest patient vitals, then session cache.
 */
export const getFrontDeskVitalSignsForVisit = async ({
  patientId,
  appointmentId,
} = {}) => {
  if (appointmentId) {
    try {
      const byAppointment = await getVitalSignsByAppointmentId(appointmentId);
      if (byAppointment.length > 0) {
        return sortByNewest(byAppointment)[0];
      }
    } catch (error) {
      console.warn(
        "[vitalSignsService] get by appointment failed:",
        error?.response?.status || error?.message
      );
    }

    const cached = readCachedFrontDeskVitalSigns(appointmentId);
    if (cached) return cached;
  }

  if (patientId) {
    try {
      const byPatient = await getVitalSignsByPatientId(patientId);
      const matched = appointmentId
        ? byPatient.filter(
            (v) => Number(v.appointmentId) === Number(appointmentId)
          )
        : byPatient;
      const list = matched.length > 0 ? matched : byPatient;
      if (list.length > 0) return sortByNewest(list)[0];
    } catch (error) {
      console.warn(
        "[vitalSignsService] get by patient failed:",
        error?.response?.status || error?.message
      );
    }
  }

  return null;
};

export default {
  getVitalSignsByPatientId,
  getVitalSignsByAppointmentId,
  createVitalSigns,
  getFrontDeskVitalSignsForVisit,
  cacheFrontDeskVitalSigns,
  readCachedFrontDeskVitalSigns,
  normalizeVitalSignsRecord,
  hasVitalSignsPrescriptionId,
  filterVitalSignsWithPrescriptionId,
  filterVitalSignsPreferPrescription,
};
